/**
 * VisaGenius guided-spark icon — canonical mark per the brand sheet.
 *
 * Four-point spark in Terracotta with a small Sage companion dot in the
 * bottom-right quadrant. Geometry comes directly from the brand sheet's
 * spark() function (viewBox 0 0 48 48; path centered around 22,22; dot
 * at 37,35 r=3.2).
 *
 * Two render modes:
 *   <Spark size={18} />                  → bare spark on transparent
 *   <Spark size={36} tile="forest" />    → spark on a rounded-square
 *                                          Forest tile (the "app icon"
 *                                          shape used in AppShell
 *                                          headers and similar chrome).
 *
 * The tile uses the brand-sheet's 23% border-radius proportion. Use
 * "forest" on Cream / paper backgrounds; "cream" on Forest / dark.
 */
import type { CSSProperties } from "react";

const SPARK_FILL = "#B5512F";
const DOT_FILL = "#D7E0CF";

export function Spark({
  size = 24,
  tile,
  className,
  style,
  sparkColor,
  dotColor,
}: {
  /** Outer box size in px. When `tile` is set, the spark renders at
   *  ~70% of this size centered on the tile. */
  size?: number;
  tile?: "forest" | "cream";
  className?: string;
  style?: CSSProperties;
  /** Override the spark color (default Terracotta). Useful for the
   *  "all forest" / "all ink" mono colorways. */
  sparkColor?: string;
  /** Override the dot color (default Sage). */
  dotColor?: string;
}) {
  const fill = sparkColor || SPARK_FILL;
  const dot = dotColor || DOT_FILL;

  const sparkPath = (
    <>
      <path
        d="M22 6 C23.4 16.6 27.4 20.6 38 22 C27.4 23.4 23.4 27.4 22 38 C20.6 27.4 16.6 23.4 6 22 C16.6 20.6 20.6 16.6 22 6 Z"
        fill={fill}
      />
      <circle cx="37" cy="35" r="3.2" fill={dot} />
    </>
  );

  if (!tile) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={className}
        style={style}
        role="img"
        aria-label="VisaGenius"
      >
        {sparkPath}
      </svg>
    );
  }

  const bg = tile === "forest" ? "#2E3A2E" : "#ECE7DC";
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        background: bg,
        borderRadius: "23%",
        flexShrink: 0,
        ...style,
      }}
      aria-label="VisaGenius"
      role="img"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={Math.round(size * 0.7)}
        height={Math.round(size * 0.7)}
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
      >
        {sparkPath}
      </svg>
    </span>
  );
}
